import { Box, Text } from '@chakra-ui/layout';
import {
  ComponentEntityType,
  componentsApi,
  ComponentType,
} from '@texas/api/endpoints/chat/componentsApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { SpinnerWithLabel } from '@texas/components/shared/SpinnerWithLabel';
import { useEffect } from 'react';
import { CountEntity } from './CountEntity';
import { ArticleEntity } from './singleEntity/ArticleEntity';
import { compareEnum } from '@texas/utils/helpers/enumHelpers';
import { CompositionGroupEntity } from './singleEntity/CompositionGroupEntity';
import { useTranslation } from 'react-i18next';

interface Props {
  componentId: number;
  componentType: ComponentType;
  entityType: ComponentEntityType;
}

export function RenderComponent({
  componentId,
  componentType,
  entityType,
}: Props) {
  const { t } = useTranslation();
  switch (componentType) {
    case 'single-entity':
      return (
        <SingleEntityComponent
          componentId={componentId}
          entityType={entityType}
        />
      );
    case 'count-entity':
      return <CountEntityComponent componentId={componentId} />;
    default:
      return <Text>{t('ai.components.unknownType')}</Text>;
  }
}

function SingleEntityComponent({
  componentId,
  entityType,
}: {
  componentId: number;
  entityType: ComponentEntityType;
}) {
  const { t } = useTranslation();
  if (
    compareEnum(ComponentEntityType, entityType, ComponentEntityType.Article)
  ) {
    return <ArticleEntity componentId={componentId} />;
  } else if (
    compareEnum(
      ComponentEntityType,
      entityType,
      ComponentEntityType.CompositionGroup,
    )
  ) {
    return <CompositionGroupEntity componentId={componentId} />;
  } else {
    return <Text>{t('ai.components.unsupportedEntityType')}</Text>;
  }
}

function CountEntityComponent({ componentId }: { componentId: number }) {
  const { refetch, loading, data, error } = useApiResource(
    componentsApi.getCountEntityComponent,
  );
  const { t } = useTranslation();

  useEffect(() => {
    refetch(componentId);
  }, [componentId, refetch]);

  return (
    <Box>
      {loading && <SpinnerWithLabel label={t('ai.components.loading')} />}
      {error && <Text>{t('ai.components.error')}</Text>}
      {data && <CountEntity component={data} />}
    </Box>
  );
}
